import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { ClipboardCheck, Calendar, Clock, User, Zap, MoreVertical, Edit, Trash2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Presenze() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventoIdParam = searchParams.get('evento');

  const [eventi, setEventi] = useState([]);
  const [presenze, setPresenze] = useState([]);
  const [volontariMap, setVolontariMap] = useState({});
  const [selectedEvento, setSelectedEvento] = useState(eventoIdParam || '');
  const [loading, setLoading] = useState(true);
  
  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingPresenza, setEditingPresenza] = useState(null);
  const [editForm, setEditForm] = useState({
    ora_checkin: '',
    ora_checkout: '',
    note: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchEventi();
    fetchVolontari();
  }, []);

  useEffect(() => {
    if (selectedEvento) {
      fetchPresenze();
    }
  }, [selectedEvento]);

  const fetchEventi = async () => {
    try {
      const response = await fetch(`${API_URL}/api/eventi`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setEventi(data);
        if (eventoIdParam) {
          setSelectedEvento(eventoIdParam);
        }
      }
    } catch (error) {
      console.error('Error fetching eventi:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVolontari = async () => {
    try {
      const response = await fetch(`${API_URL}/api/volontari`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const map = {};
        data.forEach(v => {
          map[v.volontario_id] = `${v.cognome} ${v.nome}`;
        });
        setVolontariMap(map);
      }
    } catch (error) {
      console.error('Error fetching volontari:', error);
    }
  };

  const fetchPresenze = async () => {
    try {
      const response = await fetch(`${API_URL}/api/presenze?evento_id=${selectedEvento}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPresenze(data);
      }
    } catch (error) {
      console.error('Error fetching presenze:', error);
    }
  };

  const openEditDialog = (presenza) => {
    setEditingPresenza(presenza);
    setEditForm({
      ora_checkin: presenza.ora_checkin || '',
      ora_checkout: presenza.ora_checkout || '',
      note: presenza.note || ''
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.ora_checkin) {
      toast.error('Inserisci l\'ora di inizio');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/presenze/${editingPresenza.presenza_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          ora_checkin: editForm.ora_checkin,
          ora_checkout: editForm.ora_checkout || null,
          note: editForm.note || null
        })
      });

      if (response.ok) {
        toast.success('Presenza aggiornata con successo');
        setShowEditDialog(false);
        setEditingPresenza(null);
        fetchPresenze();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Errore nell\'aggiornamento');
      }
    } catch (error) {
      console.error('Error updating presenza:', error);
      toast.error('Errore di connessione');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePresenza = async (presenza) => {
    if (!confirm(`Eliminare la presenza di ${volontariMap[presenza.volontario_id] || 'questo volontario'}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/presenze/${presenza.presenza_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Presenza eliminata');
        fetchPresenze();
      } else {
        toast.error('Errore nell\'eliminazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  // Calculate hours difference
  const calculateHours = (checkin, checkout) => {
    if (!checkin || !checkout) return null;
    try {
      const [hIn, mIn] = checkin.split(':').map(Number);
      const [hOut, mOut] = checkout.split(':').map(Number);
      const diffMinutes = (hOut * 60 + mOut) - (hIn * 60 + mIn);
      return (diffMinutes / 60).toFixed(1);
    } catch {
      return null;
    }
  };

  const selectedEventoData = eventi.find(e => e.evento_id === selectedEvento);
  const totalOre = presenze.reduce((sum, p) => sum + (p.ore_calcolate || 0), 0);

  return (
    <div className="space-y-6" data-testid="presenze-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Presenze</h1>
          <p className="page-subtitle">Visualizza e gestisci le presenze agli eventi</p>
        </div>
        <Button className="btn-success" onClick={() => navigate('/checkin')} data-testid="checkin-btn">
          <Zap className="w-4 h-4 mr-2" />
          Check-in Rapido
        </Button>
      </div>

      {/* Event Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <Select value={selectedEvento} onValueChange={setSelectedEvento}>
                <SelectTrigger data-testid="select-evento">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Seleziona un evento" />
                </SelectTrigger>
                <SelectContent>
                  {eventi.map(evento => (
                    <SelectItem key={evento.evento_id} value={evento.evento_id}>
                      {evento.titolo} - {new Date(evento.data).toLocaleDateString('it-IT')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedEventoData && (
              <div className="flex items-center gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {presenze.length} presenze
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {totalOre.toFixed(1)} ore totali
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <strong>Suggerimento:</strong> Clicca sui tre puntini a destra di ogni riga per modificare manualmente l'ora di inizio e fine. 
          Questo ti permette di correggere le presenze se il check-in non corrisponde all'effettivo orario di lavoro del volontario.
        </div>
      </div>

      {/* Presenze Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ClipboardCheck className="w-5 h-5 text-[#0055BB]" />
            {selectedEventoData ? `Presenze - ${selectedEventoData.titolo}` : 'Seleziona un evento'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner w-8 h-8" />
            </div>
          ) : !selectedEvento ? (
            <div className="empty-state py-16">
              <Calendar className="empty-state-icon" />
              <p className="empty-state-title">Seleziona un evento</p>
              <p className="empty-state-text">
                Scegli un evento dal menu per visualizzare le presenze registrate
              </p>
            </div>
          ) : presenze.length === 0 ? (
            <div className="empty-state py-16">
              <ClipboardCheck className="empty-state-icon" />
              <p className="empty-state-title">Nessuna presenza registrata</p>
              <p className="empty-state-text">
                Usa il Check-in Rapido per registrare le presenze dei volontari
              </p>
              <Button className="btn-success mt-4" onClick={() => navigate('/checkin')}>
                <Zap className="w-4 h-4 mr-2" />
                Vai al Check-in
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Volontario</TableHead>
                    <TableHead>Ora Inizio</TableHead>
                    <TableHead>Ora Fine</TableHead>
                    <TableHead>Ore</TableHead>
                    <TableHead>Modalità</TableHead>
                    <TableHead>Note</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {presenze.map((presenza) => (
                    <TableRow key={presenza.presenza_id} data-testid={`presenza-row-${presenza.presenza_id}`}>
                      <TableCell className="font-medium">
                        {volontariMap[presenza.volontario_id] || presenza.volontario_id}
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-green-600">{presenza.ora_checkin || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-red-600">{presenza.ora_checkout || '-'}</span>
                      </TableCell>
                      <TableCell>
                        {presenza.ore_calcolate ? `${presenza.ore_calcolate.toFixed(1)} h` : '-'}
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {presenza.modalita === 'manuale' ? (
                          <span className="text-amber-600 font-medium">Modificato</span>
                        ) : presenza.modalita === 'checkin_rapido' ? (
                          'Check-in Rapido'
                        ) : (
                          'Manuale'
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm max-w-[150px] truncate">
                        {presenza.note || '-'}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(presenza)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Modifica ore
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDeletePresenza(presenza)} className="text-red-600">
                              <Trash2 className="h-4 w-4 mr-2" />
                              Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      {presenze.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="text-sm text-slate-500">
                <strong>{presenze.length}</strong> volontari presenti • <strong>{totalOre.toFixed(1)}</strong> ore totali registrate
              </div>
              <p className="timestamp">
                Ultimo aggiornamento: {new Date().toLocaleString('it-IT')}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Presence Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Ore Presenza</DialogTitle>
            <DialogDescription>
              {editingPresenza && (
                <>Modifica l'orario di {volontariMap[editingPresenza.volontario_id] || 'questo volontario'}</>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ora_checkin">Ora Inizio *</Label>
                <Input
                  id="ora_checkin"
                  type="time"
                  value={editForm.ora_checkin}
                  onChange={(e) => setEditForm({...editForm, ora_checkin: e.target.value})}
                  data-testid="edit-ora-inizio"
                />
                <p className="text-xs text-muted-foreground">
                  Ora effettiva di inizio attività
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ora_checkout">Ora Fine</Label>
                <Input
                  id="ora_checkout"
                  type="time"
                  value={editForm.ora_checkout}
                  onChange={(e) => setEditForm({...editForm, ora_checkout: e.target.value})}
                  data-testid="edit-ora-fine"
                />
                <p className="text-xs text-muted-foreground">
                  Ora effettiva di fine attività
                </p>
              </div>
            </div>
            
            {/* Preview ore calcolate */}
            {editForm.ora_checkin && editForm.ora_checkout && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-green-700">Ore calcolate:</span>
                  <span className="font-bold text-green-800 text-lg">
                    {calculateHours(editForm.ora_checkin, editForm.ora_checkout)} h
                  </span>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="note">Note (opzionale)</Label>
              <Textarea
                id="note"
                value={editForm.note}
                onChange={(e) => setEditForm({...editForm, note: e.target.value})}
                placeholder="Es: Arrivato in ritardo per traffico, ma ha iniziato puntuale"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveEdit} disabled={submitting}>
              {submitting ? 'Salvataggio...' : 'Salva Modifiche'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
